import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchAllData, clearError } from './store/activeSessionsAndDevicesSlice';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeProvider } from 'styled-components';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { ConfigProvider } from 'antd';
import {
  Container,
  HeaderContainer,
  Title,
  StyledButton,
  GridContainer,
  StyledCard,
  CardTitle,
  SessionsList,
  DeviceList,
  SessionItem,
  DeviceItem,
  IconWrapper,
  ItemContent,
  ItemTitle,
  ItemSubtitle,
  ErrorContainer,
  ErrorText,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
} from './Styles';
import { ICONS, ERROR_MESSAGES, EMPTY_STATES } from './constants';
import { Device, CurrentSession } from './types';

const ActiveSessionsAndDevices: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { devices, currentSessions, loading, error } = useSelector(
    (state: RootState) => state.activeSessionsAndDevices
  );

  useEffect(() => {
    dispatch(fetchAllData());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(clearError());
    dispatch(fetchAllData());
  };

  const handleRetry = () => {
    dispatch(clearError());
    dispatch(fetchAllData());
  };

  const theme: { mode: 'light' | 'dark' } = {
    mode: isDarkMode ? 'dark' : 'light',
  };

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderContainer>
            <Title>Active Sessions & Devices</Title>
            <StyledButton onClick={handleRefresh}>Refresh</StyledButton>
          </HeaderContainer>

          {error && (
            <ErrorContainer>
              <ErrorText>{error || ERROR_MESSAGES.GENERIC}</ErrorText>
              <StyledButton onClick={handleRetry}>Retry</StyledButton>
            </ErrorContainer>
          )}

          <GridContainer>
            <StyledCard>
              <CardTitle>Current Sessions</CardTitle>
              <SessionsList>
                {loading ? (
                  <>
                    <Skeleton active avatar paragraph={{ rows: 2 }} />
                    <Skeleton active avatar paragraph={{ rows: 2 }} />
                    <Skeleton active avatar paragraph={{ rows: 2 }} />
                  </>
                ) : currentSessions && currentSessions.length > 0 ? (
                  currentSessions.map((session: CurrentSession) => (
                    <SessionItem key={session.id}>
                      <IconWrapper>
                        <FontAwesomeIcon icon={ICONS.mobile} />
                      </IconWrapper>
                      <ItemContent>
                        <ItemTitle>{session.deviceName}</ItemTitle>
                        <ItemSubtitle>
                          OS: {session.deviceOs} | IP: {session.deviceIp}
                        </ItemSubtitle>
                        <Badge
                          status="success"
                          text="Active"
                          style={{ marginTop: '4px' }}
                        />
                      </ItemContent>
                    </SessionItem>
                  ))
                ) : (
                  <EmptyState>
                    <EmptyStateIcon>
                      <FontAwesomeIcon icon={ICONS.emptySessions} />
                    </EmptyStateIcon>
                    <EmptyStateText>{EMPTY_STATES.NO_SESSIONS}</EmptyStateText>
                  </EmptyState>
                )}
              </SessionsList>
            </StyledCard>
            <StyledCard>
              <CardTitle>Devices</CardTitle>
              <DeviceList>
                {loading ? (
                  <>
                    <Skeleton active avatar paragraph={{ rows: 2 }} />
                    <Skeleton active avatar paragraph={{ rows: 2 }} />
                    <Skeleton active avatar paragraph={{ rows: 2 }} />
                  </>
                ) : devices && devices.length > 0 ? (
                  devices.map((device: Device) => (
                    <DeviceItem key={device.id}>
                      <IconWrapper>
                        <FontAwesomeIcon icon={ICONS.tablet} />
                      </IconWrapper>
                      <ItemContent>
                        <ItemTitle>{device.name}</ItemTitle>
                        <ItemSubtitle>
                          Type: {device.type.name} | ID: {device.identifier}
                        </ItemSubtitle>
                        <Badge
                          status="processing"
                          text="Registered"
                          style={{ marginTop: '4px' }}
                        />
                      </ItemContent>
                    </DeviceItem>
                  ))
                ) : (
                  <EmptyState>
                    <EmptyStateIcon>
                      <FontAwesomeIcon icon={ICONS.emptyDevices} />
                    </EmptyStateIcon>
                    <EmptyStateText>{EMPTY_STATES.NO_DEVICES}</EmptyStateText>
                  </EmptyState>
                )}
              </DeviceList>
            </StyledCard>
          </GridContainer>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default ActiveSessionsAndDevices;
